import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { ArrowLeft, Mail, MessageSquare, TrendingUp, TrendingDown, BarChart3, PieChart, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../../lib/logger';

const SimplePieChart = ({ data, colors }) => {
  if (!data || Object.keys(data).length === 0) return <div className="text-center text-slate-400 py-4">No data</div>;
  
  const total = Object.values(data).reduce((sum, val) => sum + val, 0);
  const entries = Object.entries(data).filter(([_, val]) => val > 0);
  
  return (
    <div className="space-y-2">
      {entries.map(([key, value], index) => (
        <div key={key} className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full" 
              style={{ backgroundColor: colors[index % colors.length] }}
            />
            <span className="text-sm capitalize">{key}</span>
          </div>
          <div className="text-sm">
            <span className="font-medium">{value.toLocaleString()}</span>
            <span className="text-slate-400 ml-1">({total > 0 ? ((value / total) * 100).toFixed(1) : 0}%)</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const NotificationAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/notifications/analytics');
      setAnalytics(response.data);
    } catch (error) {
      logger.error('Failed to load analytics:', error);
      toast.error('Failed to load notification analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.get('/notifications/analytics/export-pdf', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notification_analytics_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading analytics...</div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const statusColors = {
    'sent': '#22C55E',
    'failed': '#EF4444',
    'blocked': '#F59E0B',
    'pending': '#3B82F6'
  };

  return (
    <div data-testid="notification-analytics-page">
      <header className="page-header glass-header">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings/notifications')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">Notification Analytics</h1>
            <p className="text-slate-500">Monitor email and SMS delivery performance</p>
          </div>
          <Button
            onClick={handleExportPDF}
            disabled={exporting}
            size="sm"
            className="bg-red-500 hover:bg-red-600"
          >
            {exporting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</>
            ) : (
              <><Download className="w-4 h-4 mr-2" />Export PDF</>
            )}
          </Button>
        </div>
      </header>

      <div className="page-content max-w-6xl">
        <div className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Emails</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.email.total.toLocaleString()}</p>
                  </div>
                  <Mail className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Email Delivery Rate</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.email.delivery_rate}%</p>
                  </div>
                  {analytics.email.delivery_rate >= 90 ? (
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  ) : (
                    <TrendingDown className="w-8 h-8 text-red-500" />
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total SMS</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.sms.total.toLocaleString()}</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">SMS Delivery Rate</p>
                    <p className="text-2xl font-bold text-slate-900">{analytics.sms.delivery_rate}%</p>
                  </div>
                  {analytics.sms.delivery_rate >= 90 ? (
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  ) : (
                    <TrendingDown className="w-8 h-8 text-red-500" />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-blue-500" />
                  Email Status Distribution
                </CardTitle>
                <CardDescription>Breakdown by delivery status</CardDescription>
              </CardHeader>
              <CardContent>
                <SimplePieChart 
                  data={analytics.email.by_status} 
                  colors={['#22C55E', '#EF4444', '#F59E0B', '#3B82F6']} 
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-green-500" />
                  SMS Status Distribution
                </CardTitle>
                <CardDescription>Breakdown by delivery status</CardDescription>
              </CardHeader>
              <CardContent>
                <SimplePieChart 
                  data={analytics.sms.by_status} 
                  colors={['#22C55E', '#EF4444', '#F59E0B', '#3B82F6']} 
                />
              </CardContent>
            </Card>
          </div>

          {/* By Template */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Notifications by Type
              </CardTitle>
              <CardDescription>Performance by notification template</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-600">Template</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-600">Channel</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Total</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Sent</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Failed</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Blocked</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-600">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.email.by_template.map((template, index) => (
                      <tr key={`email-${template.template}`} className="border-b border-slate-100">
                        <td className="py-3 px-4 capitalize">{template.template.replace(/_/g, ' ')}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-blue-600">
                            <Mail className="w-4 h-4" />
                            Email
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-medium">{template.total}</td>
                        <td className="py-3 px-4 text-right text-green-600">{template.sent}</td>
                        <td className="py-3 px-4 text-right text-red-600">{template.failed}</td>
                        <td className="py-3 px-4 text-right text-amber-600">{template.blocked}</td>
                        <td className="py-3 px-4 text-right font-medium">
                          {template.total > 0 ? ((template.sent / template.total) * 100).toFixed(1) : 0}%
                        </td>
                      </tr>
                    ))}
                    {analytics.sms.by_template.map((template, index) => (
                      <tr key={`sms-${template.template}`} className="border-b border-slate-100">
                        <td className="py-3 px-4 capitalize">{template.template.replace(/_/g, ' ')}</td>
                        <td className="py-3 px-4 text-center">
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <MessageSquare className="w-4 h-4" />
                            SMS
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right font-medium">{template.total}</td>
                        <td className="py-3 px-4 text-right text-green-600">{template.sent}</td>
                        <td className="py-3 px-4 text-right text-red-600">{template.failed}</td>
                        <td className="py-3 px-4 text-right text-amber-600">{template.blocked}</td>
                        <td className="py-3 px-4 text-right font-medium">
                          {template.total > 0 ? ((template.sent / template.total) * 100).toFixed(1) : 0}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default NotificationAnalytics;
